import { Range, Transforms } from 'slate';
import { calculateTagSegments, insertTag, insertTagWithIds } from '../utils/insertion';
import { UseMutateFunction } from 'react-query';
import { CreateAndInsertTagParams, ContinuationTagRef, SuccessorTagRef } from '../types';
import { Tag, CreateTagInput, UpdateTagInput, SuccessResponse } from 'app/api/tag-queries/types';

/**
 * Create a tag via API and insert it into the editor
 * Handles continuation tags by updating the original tag's nextSegment
 */
export const createAndInsertTag = ({
  editor,
  tagType,
  tagData,
  continuationTag = null,
  successorTag = null,
  createTagMutation,
  continueTagMutation,
  updateTagMutation,
  onContinuationComplete,
  callbacks = {},
}: CreateAndInsertTagParams): void => {
  // Restore the last known selection if the editor lost focus (e.g. user clicked in sidebar)
  if (!editor.selection && (editor as any).lastSelection) {
    Transforms.select(editor, (editor as any).lastSelection);
  }

  if (!editor.selection || Range.isCollapsed(editor.selection)) {
    console.warn('Cannot create tag: no selection or selection is collapsed');
    return;
  }

  const isContinuation = !!continuationTag;
  const hasSuccessor = !!successorTag;
  const segmentCount = calculateTagSegments(editor);

  if (segmentCount === 0) {
    console.warn('Cannot create tag: no segments calculated');
    return;
  }

  // For overlapping tags, we need to create multiple API records
  if (segmentCount > 1) {
    createMultiSegmentTag({
      editor,
      tagType,
      tagData,
      segmentCount,
      continuationTag,
      successorTag,
      createTagMutation,
      updateTagMutation,
      onContinuationComplete,
      callbacks,
    });
    return;
  }

  // Continuation path: use atomic continueTagMutation (backend handles predecessor/successor updates)
  if (isContinuation || hasSuccessor) {
    continueTagMutation(
      {
        ...tagData,
        predecessorId: continuationTag?.tagId || undefined,
        successorId: successorTag?.tagId || undefined,
      },
      {
        onError: (error) => {
          console.error('Error creating continuation tag', error);
          callbacks.onError?.(error);
        },
        onSuccess: (newTag) => {
          if (isContinuation) {
            onContinuationComplete?.();
          }
          insertTag(editor, tagType, isContinuation ? continuationTag : null, newTag.id, hasSuccessor ? successorTag!.tagId : null);
          callbacks.onSuccess?.(newTag);
        },
      }
    );
    return;
  }

  // Non-continuation: simple create
  createTagMutation(tagData, {
    onError: (error) => {
      console.error('Error creating tag', error);
      callbacks.onError?.(error);
    },
    onSuccess: (newTag) => {
      insertTag(editor, tagType, null, newTag.id, null);
      callbacks.onSuccess?.(newTag);
    },
  });
};

interface CreateMultiSegmentTagParams {
  editor: CreateAndInsertTagParams['editor'];
  tagType: string;
  tagData: CreateTagInput;
  segmentCount: number;
  continuationTag?: ContinuationTagRef | null;
  successorTag?: SuccessorTagRef | null;
  createTagMutation: UseMutateFunction<Tag, unknown, CreateTagInput>;
  updateTagMutation: UseMutateFunction<SuccessResponse, unknown, UpdateTagInput>;
  onContinuationComplete?: () => void;
  callbacks?: {
    onSuccess?: (newTag: { id: string }) => void;
    onError?: (error: unknown) => void;
  };
}

/**
 * Create multiple tag segments via API for overlapping tag scenarios
 * Creates all API records first, then inserts into Slate with the correct IDs
 */
const createMultiSegmentTag = async ({
  editor,
  tagType,
  tagData,
  segmentCount,
  continuationTag,
  successorTag = null,
  createTagMutation,
  updateTagMutation,
  onContinuationComplete,
  callbacks = {},
}: CreateMultiSegmentTagParams): Promise<void> => {
  const isContinuation = !!continuationTag;
  const hasSuccessor = !!successorTag;
  const createdTags: { id: string }[] = [];

  // Helper to create a plain segment (no continuation linkage)
  const createSegment = (index: number): Promise<{ id: string }> => {
    return new Promise((resolve, reject) => {
      createTagMutation(tagData, {
        onError: (error) => {
          console.error(`Error creating tag segment ${index}`, error);
          reject(error);
        },
        onSuccess: (newTag) => {
          createdTags.push(newTag);
          resolve(newTag);
        },
      });
    });
  };

  try {
    // Create all segments sequentially as plain tags first
    for (let i = 0; i < segmentCount; i++) {
      await createSegment(i);
    }

    // Link internal segments via nextSegment (each segment points to the next)
    for (let i = 0; i < createdTags.length - 1; i++) {
      await new Promise<void>((resolve, reject) => {
        updateTagMutation(
          {
            id: createdTags[i].id,
            nextSegment: createdTags[i + 1].id,
            tailSegment: false,
          },
          {
            onError: reject,
            onSuccess: () => resolve(),
          }
        );
      });
    }

    // Handle continuation linkage for the last segment and predecessor atomically
    if (isContinuation || hasSuccessor) {
      if (hasSuccessor) {
        await new Promise<void>((resolve, reject) => {
          updateTagMutation(
            {
              id: createdTags[createdTags.length - 1].id,
              nextSegment: successorTag!.tagId,
              tailSegment: false,
            },
            { onError: reject, onSuccess: () => resolve() }
          );
        });
      } else {
        // Mark last segment as tail
        await new Promise<void>((resolve, reject) => {
          updateTagMutation(
            {
              id: createdTags[createdTags.length - 1].id,
              tailSegment: true,
            },
            { onError: reject, onSuccess: () => resolve() }
          );
        });
      }

      // Link predecessor -> first segment
      if (isContinuation && continuationTag?.tagId) {
        await new Promise<void>((resolve, reject) => {
          updateTagMutation(
            {
              id: continuationTag.tagId,
              nextSegment: createdTags[0].id,
              tailSegment: false,
            },
            {
              onError: (error) => {
                console.error('Error updating original tag nextSegment', error);
                reject(error);
              },
              onSuccess: () => {
                onContinuationComplete?.();
                resolve();
              },
            }
          );
        });
      }
    }

    // Insert tags into Slate with the API's ids
    const tagIds = createdTags.map((t) => t.id);
    insertTagWithIds(editor, tagType, continuationTag || null, tagIds, hasSuccessor ? successorTag!.tagId : null);

    callbacks.onSuccess?.(createdTags[0]);
  } catch (error) {
    console.error('Error creating multi-segment tag', error);
    callbacks.onError?.(error);
  }
};
